import type { AltYChatMessage } from '../types/global';

export type CanvasChatView = {
  chatText: string;
  writingCanvas: boolean;
  canvasUpdated: boolean;
};

const CANVAS_KEY_RE = /["']alty_canvas["']\s*:/;

function isCanvasFenceInner(inner: string): boolean {
  const trimmed = String(inner).trim();
  if (!trimmed.includes('alty_canvas')) return false;
  try {
    const parsed = JSON.parse(trimmed);
    return !!(parsed && typeof parsed === 'object' && parsed.alty_canvas);
  } catch {
    return CANVAS_KEY_RE.test(trimmed);
  }
}

function isOpenCanvasFence(inner: string, canvasMode?: boolean): boolean {
  if (isCanvasFenceInner(inner)) return true;
  return !!(canvasMode && /^\s*\{/.test(inner));
}

// Hide `alty_canvas` JSON blocks from chat — the left workspace canvas owns
// that content. While the model is still streaming a block, callers show a
// compact badge instead of the raw fenced JSON.
export function formatAssistantCanvasChat(
  text: string,
  options?: { canvasMode?: boolean },
): CanvasChatView {
  // No fenced block at all → nothing to inspect, nothing to hide. Hot path for
  // streaming chat-bubble text in canvas mode (where the prior precheck below
  // intentionally falls through to flag a half-typed `{` as an open canvas
  // fence). Without this guard, every rAF tick re-runs the two regex passes
  // below over the full growing assistant text, which sums to quadratic work
  // for long replies and is the dominant cost of slow streaming in canvas mode.
  if (!text.includes('```')) {
    return { chatText: text, writingCanvas: false, canvasUpdated: false };
  }
  if (!text.includes('alty_canvas') && !options?.canvasMode) {
    return { chatText: text, writingCanvas: false, canvasUpdated: false };
  }

  // Phase-2 hot path: text has EXACTLY one open fence with nothing closing it
  // (the canvas JSON streaming in). The chatText is the stable prefix before
  // the open fence; the canvas body itself never appears in the bubble. We can
  // decide `writingCanvas` from a bounded peek at the inner instead of scanning
  // the growing body with the two regex passes below — which is what made
  // streaming feel slow once the assistant started emitting the canvas block.
  const firstFenceAt = text.indexOf('```');
  if (firstFenceAt >= 0 && text.indexOf('```', firstFenceAt + 3) === -1) {
    const innerStart = firstFenceAt + 3;
    const peek = text.slice(innerStart, innerStart + 1024);
    const innerBody = peek.replace(/^(?:json)?\s*/i, '');
    const isCanvas = innerBody.includes('alty_canvas')
      || (options?.canvasMode && /^\s*\{/.test(innerBody));
    const prefix = text.slice(0, firstFenceAt);
    return {
      chatText: prefix.replace(/\n{3,}/g, '\n\n').trim(),
      writingCanvas: !!isCanvas,
      canvasUpdated: false,
    };
  }

  let writingCanvas = false;
  let canvasUpdated = false;
  const withoutComplete = text.replace(/```(?:json)?\s*([\s\S]*?)```/gi, (full, inner) => {
    if (!isCanvasFenceInner(inner)) return full;
    canvasUpdated = true;
    return '';
  });

  const tail = withoutComplete.trimEnd();
  const openFence = /```(?:json)?\s*[\s\S]*$/.exec(tail);
  if (openFence) {
    const inner = openFence[0].replace(/^```(?:json)?\s*/i, '');
    if (isOpenCanvasFence(inner, options?.canvasMode)) {
      writingCanvas = true;
    }
  }

  const chatText = (writingCanvas
    ? tail.slice(0, tail.length - (openFence?.[0].length || 0))
    : withoutComplete
  ).replace(/\n{3,}/g, '\n\n').trim();

  return { chatText, writingCanvas, canvasUpdated };
}

export function latestAssistantText(messages: AltYChatMessage[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role === 'assistant') return message.text || '';
  }
  return '';
}
